/// <amd-module name="Wit/Common/Utils/WorkItemUtils/RecycleBinWorkItemsDialog" />
import * as React from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Dialog } from "VSSUI/Dialog";
import { Observer } from "VSSUI/Observer";
import { ProgressBar } from "VSSUI/ProgressBar";
import { Spinner, SpinnerOrientation, SpinnerSize } from "VSSUI/Spinner";
import * as Resources from "Wit/Common/Utils/WorkItemUtils/Resources";
import { PageSizes } from "Wit/WorkItemModel/OM/";
import { executeCallbackForTargetIdsInBatches } from "Wit/Common/WorkItemDialogs/Components/BatchExecutionHelper";

export interface IRecycleBinWorkItemsDialogProps {
    workItemIds: ObservableArray<string>;
    onDismiss: () => void;
    recycleBinRequestType: RecycleBinRequestType;
    callBack?: () => void;
    maximumAmountOfSimultaneousRequests?: number;
}

interface IRecycleBinWorkItemsDialogState {
    errorMessage: string | undefined;
    operationStatus: RecycleBinOperationStatus;
}

enum RecycleBinOperationStatus {
    /**
     * The operation is waiting in a queue and has not yet started.
     */
    Queued = 1,
    /**
     * The operation is currently in progress.
     */
    InProgress = 2,
    /**
     * The operation has completed.
     */
    Completed = 3,
    /**
     * The operation has failed. Check for an error message.
     */
    Failed = 4
}

export enum RecycleBinRequestType {
    NotSet = 0,
    Destroy = 1,
    Restore = 2
}

interface IRecycleBinWorkItemsRequest {
    ids: number[];
    recycleBinRequestType: RecycleBinRequestType;
}

interface IRecycleBinWorkItemsResponse {
    successes: number[];
    failures: number[];
}

export class RecycleBinWorkItemsDialog extends VssComponent<IRecycleBinWorkItemsDialogProps, IRecycleBinWorkItemsDialogState> {
    private stepNumber = new ObservableValue<number>(0);
    private stepCount = new ObservableValue<number>(0);
    private failedIds: number[] | undefined = undefined;
    private defaultMaximumAmountOfSimultaneousRequests = 5;

    public constructor(props: IRecycleBinWorkItemsDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorMessage: undefined,
            operationStatus: RecycleBinOperationStatus.Queued
        };
    }

    public async componentDidMount(): Promise<void> {
        const ids = this.props.workItemIds.value.map(i => +i).filter(id => !!id);
        this.setState({ operationStatus: RecycleBinOperationStatus.InProgress });
        const succeeded = await executeCallbackForTargetIdsInBatches(
            ids,
            subsetTargetIds => {
                return this.recycleBinProcessBatchOfWorkItems(subsetTargetIds, this.props.recycleBinRequestType);
            },
            this.stepCount,
            this.stepNumber,
            this.props.maximumAmountOfSimultaneousRequests ?? this.defaultMaximumAmountOfSimultaneousRequests
        );

        if (succeeded) {
            this.setState({ operationStatus: RecycleBinOperationStatus.Completed });
            this.props.callBack && this.props.callBack();
            this.props.onDismiss();
        } else {
            this.setState({ operationStatus: RecycleBinOperationStatus.Failed });
        }
    }

    private recycleBinProcessBatchOfWorkItems = async (workItemIds: number[], recycleBinRequestType: RecycleBinRequestType): Promise<boolean> => {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");

        const recycleBinWorkItemsResponse = await contributionService.getDataAsync<IRecycleBinWorkItemsResponse, IRecycleBinWorkItemsRequest>(
            "ms.vss-work-web.recyle-bin-work-items-data-provider",
            { ids: workItemIds, recycleBinRequestType: recycleBinRequestType },
            true
        );

        if (!recycleBinWorkItemsResponse) {
            this.failedIds = [...new Set([...(this.failedIds ?? []), ...workItemIds])];
            this.setState({ operationStatus: RecycleBinOperationStatus.Failed });
        }

        if (recycleBinWorkItemsResponse?.failures && recycleBinWorkItemsResponse.failures.length > 0) {
            this.failedIds = [...new Set([...(this.failedIds ?? []), ...recycleBinWorkItemsResponse.failures])];
            this.setState({ operationStatus: RecycleBinOperationStatus.Failed });
        }

        return !!recycleBinWorkItemsResponse;
    };

    public render() {
        return (
            <Observer stepNumber={this.stepNumber} stepCount={this.stepCount}>
                {(observableProps: { stepNumber: number; stepCount: number }) => {
                    const useRestoreTemplates = this.props.recycleBinRequestType === RecycleBinRequestType.Restore;
                    let title = useRestoreTemplates ? Resources.RestoringWorkItemTitle : Resources.PermanentlyDeletingWorkItemTitle;
                    let content = <Spinner className="padding-vertical-8" size={SpinnerSize.large} />;

                    switch (this.state.operationStatus) {
                        case RecycleBinOperationStatus.InProgress: {
                            const processingTemlate = useRestoreTemplates
                                ? Resources.RestoreProcessingMessage
                                : Resources.PermanentlyDeleteProcessingMessage;
                            const stepMultiplier = PageSizes.QUERY;
                            const from = observableProps.stepNumber * stepMultiplier + 1;
                            const requestCount = this.props.maximumAmountOfSimultaneousRequests ?? this.defaultMaximumAmountOfSimultaneousRequests;
                            const to = (observableProps.stepNumber + requestCount) * stepMultiplier;
                            const processingMessage = format(
                                processingTemlate,
                                from,
                                to < this.props.workItemIds.value.length ? to : this.props.workItemIds.value.length,
                                this.props.workItemIds.value.length
                            );
                            content = (
                                <>
                                    <ProgressBar
                                        className="flex-self-stretch"
                                        currentValue={observableProps.stepNumber}
                                        maxValue={observableProps.stepCount}
                                    />
                                    <Spinner
                                        className="font-size-ms padding-vertical-16"
                                        label={processingMessage}
                                        orientation={SpinnerOrientation.row}
                                    />
                                </>
                            );
                            break;
                        }
                        case RecycleBinOperationStatus.Completed: {
                            const successTitleTemplate = useRestoreTemplates
                                ? Resources.RestoreSucceededTitle
                                : Resources.PermanentlyDeleteSucceededTitle;
                            const successMessageTemplate = useRestoreTemplates
                                ? Resources.RestoreSucceededMessage
                                : Resources.PermanentlyDeleteSucceededMessage;
                            title = successTitleTemplate;
                            content = (
                                <div className="scroll-auto flex-grow flex-column">
                                    <div>
                                        <p>{format(successMessageTemplate, this.props.workItemIds.length)}</p>
                                    </div>
                                </div>
                            );
                            break;
                        }
                        case RecycleBinOperationStatus.Failed: {
                            const errorTitleTemplate = useRestoreTemplates ? Resources.RestoreFailedTitle : Resources.PermanentlyDeleteFailedTitle;
                            const errorMessageTemplate = useRestoreTemplates
                                ? Resources.RestoreFailedMessage
                                : Resources.PermanentlyDeleteFailedMessage;
                            title = errorTitleTemplate;
                            content = (
                                <div className="scroll-auto flex-grow flex-column">
                                    <div>
                                        <p>
                                            {format(
                                                errorMessageTemplate,
                                                this.failedIds ? this.failedIds.join(", ") : this.props.workItemIds.value.join(", ")
                                            )}
                                        </p>
                                    </div>
                                </div>
                            );
                            break;
                        }
                        case RecycleBinOperationStatus.Queued:
                        default: {
                            break;
                        }
                    }

                    return (
                        <Dialog
                            titleProps={{ text: title }}
                            onDismiss={this.props.onDismiss}
                            escDismiss={false}
                            lightDismiss={false}
                            footerButtonProps={[
                                {
                                    text: Resources.OK,
                                    onClick: this.props.onDismiss,
                                    primary: true,
                                    disabled: this.state.operationStatus === RecycleBinOperationStatus.InProgress
                                }
                            ]}
                        >
                            {content}
                        </Dialog>
                    );
                }}
            </Observer>
        );
    }
}
